/**
 * `/casos/:id/resultado` (E153.F3, GUS-176, ADR-0104 §2.6): o resultado auditado do caso na área da
 * equipe — o que o sistema preparou para compor o laudo e as demais saídas. O link sai da
 * conferência das etapas e da formalização preparada na mesa. A página mostra; não decide nem
 * registra: a situação, o consumível e a vigência de cada artefato vêm do servidor.
 */
import { Link, useParams } from 'react-router-dom';

import { FaixaDoProximoPasso } from '@/components/ProximoPasso/ProximoPasso';
import { ResultadoAuditado } from '@/components/ResultadoAuditado';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { useResultadoDoCaso } from '@/lib/useResultadoDoCaso';

const t = ptBR.resultadoAuditado;

export function ResultadoDoCasoPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const client = useApiClient();
  const { resultado, reler } = useResultadoDoCaso(client, caseId);

  return (
    <section
      className="mx-auto flex max-w-5xl flex-col gap-sc-6 p-sc-6"
      data-testid="resultado-page"
      data-estado={resultado.estado}
    >
      <header>
        <h1 className="text-display-sm text-fg">{t.titulo}</h1>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.subtitulo}</p>
        <nav className="mt-sc-2 flex flex-wrap gap-sc-4 text-body-sm">
          <Link className="text-accent-700" to={`/casos/${caseId}`}>
            {t.voltar}
          </Link>
          {/* E158.F3 (GUS-191): a minuta das orientações reservadas. */}
          <Link
            className="text-accent-700"
            to={`/casos/${caseId}/orientacoes`}
            data-testid="resultado-orientacoes"
          >
            {ptBR.orientacoes.equipe.abrir}
          </Link>
          {/* E159.F3 (GUS-153): a conferência do laudo e das orientações antes da assinatura. */}
          <Link
            className="text-accent-700"
            to={`/casos/${caseId}/saidas`}
            data-testid="resultado-saidas"
          >
            {ptBR.conferenciaDasSaidas.linkParaAConferencia}
          </Link>
        </nav>
      </header>
      {resultado.estado === 'carregando' ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : null}
      {resultado.estado === 'erro' ? (
        <div role="alert" className="space-y-sc-2" data-testid="resultado-erro">
          <p className="text-body-sm text-danger-700">{t.recusa[resultado.motivo]}</p>
          {resultado.motivo === 'indisponivel' ? (
            <Button type="button" variant="secondary" size="sm" onClick={reler}>
              {t.tentarDeNovo}
            </Button>
          ) : null}
        </div>
      ) : null}
      {resultado.estado === 'pronto' ? (
        <>
          {/* E162.F2.T01: o próximo passo da prontidão que esta página já leu. */}
          <FaixaDoProximoPasso caseId={caseId} prontidao={resultado.dados.prontidao} />
          <ResultadoAuditado dados={resultado.dados} />
        </>
      ) : null}
    </section>
  );
}
